import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  createChannel,
  getChannel,
  getChannelPackages,
  getChannelPolicies,
  listChannels,
  putChannelPolicies,
} from './channelsApi';
import { listPolicies } from './policiesApi';

const SUMMARY_MAX_LENGTH = 80;
const CHANNEL_NAME_RE = /^[a-z0-9_-]{1,64}$/;

function validateChannelName(name) {
  if (!name) return 'a name is required';
  if (!CHANNEL_NAME_RE.test(name)) {
    return 'must be 1-64 characters: lowercase letters, digits, hyphens (-), and underscores (_) only';
  }
  return null;
}

function channelKey(channel) {
  return `${channel.owner_username}/${channel.name}`;
}

// Collapsed to one line and capped, same convention as Policies.js's
// `summarize` - `description` can run long enough that a list item
// shouldn't just dump it verbatim.
function summarize(text, max = SUMMARY_MAX_LENGTH) {
  const singleLine = text.replace(/\s+/g, ' ').trim();
  if (singleLine.length <= max) return singleLine;
  return `${singleLine.slice(0, max - 1).trimEnd()}\u2026`;
}

function channelLabel(channel) {
  const key = channelKey(channel).toUpperCase();
  const visibility = channel.visibility === 'private' ? 'PRIVATE' : 'PUBLIC';
  const description = channel.description
    ? summarize(channel.description)
    : 'NO DESCRIPTION ON FILE';
  return `${key} (${visibility}) \u2014 ${description}`;
}

function formatTimestamp(iso) {
  if (!iso) return 'UNKNOWN';
  try {
    return new Date(iso).toLocaleString();
  } catch (err) {
    return iso;
  }
}

// One bound policy (BindingOut in schemas_policy.py), as shown in the
// read-only bindings list.
function bindingLabel(binding) {
  const status = binding.enabled ? 'ENABLED' : 'DISABLED';
  return (
    `${binding.policy.toUpperCase()} \u2014 REV ${binding.revision},`
    + ` DEFAULT ${binding.default_effect.toUpperCase()}, ${status}`
  );
}

// One option in the "add a policy" dropdown, drawn from PolicyOut - a
// bindable policy's own visibility, not the binding's enabled state
// (there is no binding yet).
function policyOptionLabel(policy) {
  const key = `${policy.owner_username}/${policy.name}`.toUpperCase();
  const visibility = policy.visibility === 'private' ? 'PRIVATE' : 'PUBLIC';
  return `${key} (${visibility})`;
}

export default function Channels({ onError }) {
  const auth = useAuth();
  const accessToken = auth.user?.access_token;
  const username = auth.user?.profile?.preferred_username
    ?? auth.user?.profile?.username
    ?? auth.user?.profile?.sub;

  // `view` mirrors Policies.js's pattern: 'list' shows every channel the
  // caller owns (public or private - GET /api/channels?owner=<self>);
  // 'detail' drills into one, keyed by `{owner, name}` since that's how
  // the backend addresses a channel (there's no separate channel id
  // route). 'create' is the "+ NEW CHANNEL" form, entered from 'list' -
  // same shape as DraftPolicies.js's new-draft form.
  const [view, setView] = useState('list');

  const [status, setStatus] = useState('loading'); // loading | error | ready
  const [channels, setChannels] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [nextCursor, setNextCursor] = useState(null);
  const [loadMoreStatus, setLoadMoreStatus] = useState('idle'); // idle | loading

  // A one-line success note shown on the list view after creating a
  // channel - same convention as DraftPolicies.js's `listNotice`.
  // Cleared on the next list refresh so it can't go stale.
  const [listNotice, setListNotice] = useState('');

  // The "+ NEW CHANNEL" form.
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newVisibility, setNewVisibility] = useState('public');
  const [createFormError, setCreateFormError] = useState('');
  const [createStatus, setCreateStatus] = useState('idle'); // idle | submitting

  const [detailKey, setDetailKey] = useState(null); // { owner, name } | null
  const [detailStatus, setDetailStatus] = useState('idle'); // idle | loading | error | ready
  const [detailError, setDetailError] = useState('');
  const [detailChannel, setDetailChannel] = useState(null);
  const [bindings, setBindings] = useState([]);
  const [admitsAll, setAdmitsAll] = useState(false);
  const [detailNotice, setDetailNotice] = useState('');
  // `{ packageCount, names } | null` - null until loaded (or if the
  // packages call failed; the rest of the detail view still renders).
  const [detailPackages, setDetailPackages] = useState(null);

  // The policy-binding editor, entered from a channel's detail view.
  // `draftBindings` is a local, reorderable working copy - `{ policy,
  // enabled }[]` in the order that will be sent to PUT
  // .../policies (see BindingsPut in schemas_policy.py) - nothing is
  // written until SAVE. Separate from `bindings` (the last-saved,
  // server-confirmed list) so CANCEL can simply discard the draft.
  const [editing, setEditing] = useState(false);
  const [draftBindings, setDraftBindings] = useState([]);
  const [eligiblePolicies, setEligiblePolicies] = useState([]);
  const [eligibleStatus, setEligibleStatus] = useState('idle'); // idle | loading | error | ready
  const [eligibleError, setEligibleError] = useState('');
  const [addPolicyKey, setAddPolicyKey] = useState('');
  const [editFormError, setEditFormError] = useState('');
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving

  const fetchChannels = useCallback(async () => {
    if (!username) return;
    setStatus('loading');
    try {
      const result = await listChannels(accessToken, { owner: username });
      setChannels(result.items);
      setNextCursor(result.nextCursor);
      setStatus('ready');
    } catch (err) {
      setErrorMessage(err.message || 'Could not load channels. Please try again.');
      setStatus('error');
    }
  }, [accessToken, username]);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  async function handleLoadMore() {
    if (!nextCursor || loadMoreStatus === 'loading') return;
    setLoadMoreStatus('loading');
    try {
      const result = await listChannels(accessToken, { owner: username, cursor: nextCursor });
      setChannels((prev) => [...prev, ...result.items]);
      setNextCursor(result.nextCursor);
      setLoadMoreStatus('idle');
    } catch (err) {
      setErrorMessage(err.message || 'Could not load more channels. Please try again.');
      setLoadMoreStatus('idle');
      onError?.();
    }
  }

  // --- "+ NEW CHANNEL" form ---

  function resetCreateForm() {
    setNewName('');
    setNewDescription('');
    setNewVisibility('public');
    setCreateFormError('');
    setCreateStatus('idle');
  }

  function openCreate() {
    setView('create');
    setListNotice('');
    resetCreateForm();
  }

  function backToListFromCreate() {
    setView('list');
    resetCreateForm();
  }

  // Every place that shows an inline `createFormError` also raises the
  // fixed bottom error-toast (App.js's `onError`) - same convention as
  // DraftPolicies.js's `reportFormError`.
  function reportCreateFormError(message) {
    setCreateFormError(message);
    onError?.();
  }

  async function handleSubmitCreate(event) {
    event.preventDefault();
    const nameError = validateChannelName(newName);
    if (nameError) {
      reportCreateFormError(nameError);
      return;
    }
    setCreateFormError('');
    setCreateStatus('submitting');
    try {
      await createChannel(accessToken, {
        name: newName,
        description: newDescription,
        visibility: newVisibility,
      });
      backToListFromCreate();
      setListNotice('THE CHANNEL HAS BEEN CLAIMED.');
      fetchChannels();
    } catch (err) {
      reportCreateFormError(err.message || 'Could not create the channel. Please try again.');
      setCreateStatus('idle');
    }
  }

  const fetchDetail = useCallback(async (owner, name) => {
    setDetailStatus('loading');
    setDetailError('');
    try {
      const [channelResult, policiesResult, packagesResult] = await Promise.all([
        getChannel(accessToken, owner, name),
        getChannelPolicies(accessToken, owner, name),
        getChannelPackages(accessToken, owner, name).catch(() => null),
      ]);
      setDetailChannel(channelResult);
      setBindings(policiesResult.items);
      setAdmitsAll(policiesResult.admits_all);
      setDetailPackages(packagesResult);
      setDetailStatus('ready');
    } catch (err) {
      setDetailError(err.message || 'Could not load this channel. Please try again.');
      setDetailStatus('error');
    }
  }, [accessToken]);

  function openDetail(owner, name) {
    setView('detail');
    setListNotice('');
    setDetailKey({ owner, name });
    setDetailChannel(null);
    setBindings([]);
    setAdmitsAll(false);
    setDetailPackages(null);
    setDetailError('');
    setDetailNotice('');
    setEditing(false);
    fetchDetail(owner, name);
  }

  function backToList() {
    setView('list');
    setDetailKey(null);
    setDetailStatus('idle');
    setDetailError('');
    setDetailChannel(null);
    setBindings([]);
    setDetailPackages(null);
    setDetailNotice('');
    setEditing(false);
  }

  // --- Policy-binding editor ---

  const fetchEligiblePolicies = useCallback(async () => {
    setEligibleStatus('loading');
    setEligibleError('');
    try {
      // Own + every public policy (mirrors listOwnPolicies in
      // draftsApi.js's cap of 100 - plenty for a picker; there's no
      // "policies bindable by me" endpoint to query more precisely).
      const result = await listPolicies(accessToken, { limit: 100 });
      setEligiblePolicies(result.items);
      setEligibleStatus('ready');
    } catch (err) {
      setEligibleError(err.message || 'Could not load policies. Please try again.');
      setEligibleStatus('error');
    }
  }, [accessToken]);

  function startEditing() {
    setEditing(true);
    setEditFormError('');
    setDetailNotice('');
    setDraftBindings(bindings.map((b) => ({ policy: b.policy, enabled: b.enabled })));
    setAddPolicyKey('');
    fetchEligiblePolicies();
  }

  function cancelEditing() {
    setEditing(false);
    setEditFormError('');
    setDraftBindings([]);
  }

  function handleAddBinding() {
    if (!addPolicyKey) return;
    setDraftBindings((prev) => {
      if (prev.some((b) => b.policy === addPolicyKey)) return prev;
      return [...prev, { policy: addPolicyKey, enabled: true }];
    });
    setAddPolicyKey('');
  }

  function handleRemoveBinding(index) {
    setDraftBindings((prev) => prev.filter((_, i) => i !== index));
  }

  function handleToggleBinding(index) {
    setDraftBindings((prev) =>
      prev.map((b, i) => (i === index ? { ...b, enabled: !b.enabled } : b))
    );
  }

  function handleMoveBinding(index, direction) {
    setDraftBindings((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  async function handleSaveBindings(event) {
    event.preventDefault();
    setEditFormError('');
    setSaveStatus('saving');
    try {
      const result = await putChannelPolicies(
        accessToken,
        detailKey.owner,
        detailKey.name,
        draftBindings,
      );
      setBindings(result.items);
      setAdmitsAll(result.admits_all);
      setEditing(false);
      setSaveStatus('idle');
      setDetailNotice('THE CHANNEL\u2019S POLICIES HAVE BEEN REBOUND.');
    } catch (err) {
      setEditFormError(err.message || 'Could not save these bindings. Please try again.');
      setSaveStatus('idle');
      onError?.();
    }
  }

  const isOwner = Boolean(username) && detailChannel?.owner_username === username;

  const eligibleOptions = eligiblePolicies.filter(
    (p) => !draftBindings.some((b) => b.policy === `${p.owner_username}/${p.name}`),
  );

  return (
    <div className="terminal-box view-panel">
      <p className="form-title">== CHANNELS ==</p>

      {view === 'list' && status === 'loading' && (
        <p className="dim-line">SCANNING THE COUNTRYSIDE FOR THY CHANNELS...</p>
      )}

      {view === 'list' && status === 'error' && (
        <>
          <p className="error-line">! {errorMessage}</p>
          <div className="button-row">
            <button
              type="button"
              className="retro-button retro-button--ghost"
              onClick={fetchChannels}
            >
              RETRY
            </button>
          </div>
        </>
      )}

      {view === 'list' && status === 'ready' && listNotice && (
        <p className="success-line">{listNotice}</p>
      )}

      {view === 'list' && status === 'ready' && channels.length === 0 && (
        <p className="dim-line">
          NO CHANNELS ON FILE. THE COUNTRYSIDE HAS YET TO BE ORGANIZED INTO
          CHANNELS OF ITS OWN.
        </p>
      )}

      {view === 'list' && status === 'ready' && channels.length > 0 && (
        <>
          <ul className="view-list">
            {channels.map((channel) => (
              <li key={channel.id}>
                <button
                  type="button"
                  className="view-list-item"
                  onClick={() => openDetail(channel.owner_username, channel.name)}
                >
                  {channelLabel(channel)}
                </button>
              </li>
            ))}
          </ul>

          {nextCursor && (
            <div className="button-row">
              <button
                type="button"
                className="retro-button retro-button--ghost"
                onClick={handleLoadMore}
                disabled={loadMoreStatus === 'loading'}
              >
                {loadMoreStatus === 'loading' ? 'LOADING...' : 'LOAD MORE'}
              </button>
            </div>
          )}
        </>
      )}

      {view === 'list' && status === 'ready' && (
        <div className="button-row">
          <button type="button" className="retro-button" onClick={openCreate}>
            + NEW CHANNEL
          </button>
        </div>
      )}

      {view === 'create' && (
        <form onSubmit={handleSubmitCreate}>
          <p className="dim-line">
            CLAIM A NEW CHANNEL OF THY OWN; NO OTHER VILLAGER MAY BURNINATE
            UNDER THIS NAME.
          </p>

          <label className="form-row" htmlFor="channel-name">
            <span>NAME OF THE CHANNEL:</span>
            <input
              id="channel-name"
              name="channel-name"
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value.toLowerCase())}
              disabled={createStatus === 'submitting'}
              spellCheck="false"
              autoFocus
            />
          </label>

          <label className="form-row" htmlFor="channel-description">
            <span>DESCRIPTION (OPTIONAL):</span>
            <textarea
              id="channel-description"
              name="channel-description"
              rows={4}
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              disabled={createStatus === 'submitting'}
              placeholder="E.G. A MIRROR OF EVERYTHING NUMPY, BUT NEVER ANYTHING WITH SCALES"
            />
          </label>

          <label className="form-row" htmlFor="channel-visibility">
            <span>VISIBILITY:</span>
            <select
              id="channel-visibility"
              name="channel-visibility"
              value={newVisibility}
              onChange={(e) => setNewVisibility(e.target.value)}
              disabled={createStatus === 'submitting'}
            >
              <option value="public">PUBLIC — ANY VILLAGER MAY SEE IT</option>
              <option value="private">PRIVATE — FOR THY EYES ONLY</option>
            </select>
          </label>

          {createFormError && <p className="error-line">! {createFormError}</p>}

          <div className="button-row">
            <button type="submit" className="retro-button" disabled={createStatus === 'submitting'}>
              {createStatus === 'submitting' ? 'CLAIMING...' : 'CREATE CHANNEL'}
            </button>
            <button
              type="button"
              className="retro-button retro-button--ghost"
              onClick={backToListFromCreate}
              disabled={createStatus === 'submitting'}
            >
              CANCEL
            </button>
          </div>
        </form>
      )}

      {view === 'detail' && detailStatus === 'loading' && (
        <p className="dim-line">LOADING THIS CHANNEL...</p>
      )}

      {view === 'detail' && detailStatus === 'error' && (
        <>
          <p className="error-line">! {detailError}</p>
          <div className="button-row">
            <button
              type="button"
              className="retro-button retro-button--ghost"
              onClick={() => fetchDetail(detailKey.owner, detailKey.name)}
            >
              RETRY
            </button>
            <button
              type="button"
              className="retro-button retro-button--ghost"
              onClick={backToList}
            >
              &laquo; BACK
            </button>
          </div>
        </>
      )}

      {view === 'detail' && detailStatus === 'ready' && detailChannel && (
        <>
          <p className="dim-line">
            {channelKey(detailChannel).toUpperCase()}
            {' \u2014 '}
            {detailChannel.visibility === 'private' ? 'PRIVATE' : 'PUBLIC'}
          </p>
          <p className="dim-line">
            {detailChannel.description
              ? detailChannel.description
              : 'NO DESCRIPTION ON FILE.'}
          </p>
          <p className="dim-line">CREATED {formatTimestamp(detailChannel.created_at)}</p>
          <p className="dim-line">ADMITS ALL PACKAGES: {admitsAll ? 'YES' : 'NO'}</p>

          {!editing && detailNotice && <p className="success-line">{detailNotice}</p>}

          {!editing && detailPackages && (
            <>
              <p className="form-title form-title--section">
                == PACKAGES ({detailPackages.packageCount}) ==
              </p>
              {detailPackages.names.length === 0 ? (
                <p className="dim-line">
                  NO PACKAGES ADMITTED TO THIS CHANNEL YET.
                </p>
              ) : (
                <ul className="view-list">
                  {detailPackages.names.map((packageName) => (
                    <li key={packageName}>{packageName.toUpperCase()}</li>
                  ))}
                  {detailPackages.packageCount > detailPackages.names.length && (
                    <li className="dim-line">
                      {'\u2026'}AND {detailPackages.packageCount - detailPackages.names.length} MORE
                    </li>
                  )}
                </ul>
              )}
            </>
          )}

          {!editing && (
            <>
              <p className="form-title form-title--section">== BOUND POLICIES ==</p>

              {bindings.length === 0 ? (
                <p className="dim-line">
                  NO LAWS BOUND TO THIS CHANNEL. EVERY PACKAGE MEETS AN
                  UNDEFINED FATE UNTIL SOME POLICY IS ATTACHED.
                </p>
              ) : (
                <ul className="view-list">
                  {bindings.map((binding, index) => (
                    <li key={binding.policy_id}>
                      #{index + 1} {'\u2014'} {bindingLabel(binding)}
                    </li>
                  ))}
                </ul>
              )}

              <div className="button-row button-row--split">
                <button
                  type="button"
                  className="retro-button retro-button--ghost"
                  onClick={backToList}
                >
                  &laquo; BACK
                </button>
                {isOwner && (
                  <button type="button" className="retro-button" onClick={startEditing}>
                    ADJUST POLICIES
                  </button>
                )}
              </div>
            </>
          )}

          {editing && (
            <form onSubmit={handleSaveBindings}>
              <p className="form-title form-title--section">== ADJUST BOUND POLICIES ==</p>
              <p className="dim-line">
                ORDER IS LAW: THE LAST MATCHING RULE ACROSS ALL BOUND POLICIES,
                IN THE ORDER SHOWN BELOW, DECIDES A PACKAGE&apos;S FATE.
              </p>

              {draftBindings.length === 0 ? (
                <p className="dim-line">
                  NOTHING BOUND YET. ADD A LAW BELOW TO GIVE THIS CHANNEL SOME
                  RULES TO LIVE BY.
                </p>
              ) : (
                <ul className="view-list">
                  {draftBindings.map((binding, index) => (
                    <li key={binding.policy} className="binding-row">
                      <span className="binding-row__ordinal">#{index + 1}</span>
                      <span className="binding-row__policy">{binding.policy.toUpperCase()}</span>
                      <label className="binding-row__enabled">
                        <input
                          type="checkbox"
                          checked={binding.enabled}
                          onChange={() => handleToggleBinding(index)}
                          disabled={saveStatus === 'saving'}
                        />
                        ENABLED
                      </label>
                      <div className="binding-row__controls">
                        <button
                          type="button"
                          className="retro-button retro-button--ghost retro-button--icon"
                          aria-label={`Move ${binding.policy} up`}
                          onClick={() => handleMoveBinding(index, -1)}
                          disabled={index === 0 || saveStatus === 'saving'}
                        >
                          &uarr;
                        </button>
                        <button
                          type="button"
                          className="retro-button retro-button--ghost retro-button--icon"
                          aria-label={`Move ${binding.policy} down`}
                          onClick={() => handleMoveBinding(index, 1)}
                          disabled={index === draftBindings.length - 1 || saveStatus === 'saving'}
                        >
                          &darr;
                        </button>
                        <button
                          type="button"
                          className="retro-button retro-button--danger retro-button--icon"
                          onClick={() => handleRemoveBinding(index)}
                          disabled={saveStatus === 'saving'}
                        >
                          REMOVE
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {eligibleStatus === 'loading' && (
                <p className="dim-line">CONSULTING THE LEDGER FOR BINDABLE LAWS...</p>
              )}

              {eligibleStatus === 'error' && (
                <>
                  <p className="error-line">! {eligibleError}</p>
                  <div className="button-row">
                    <button
                      type="button"
                      className="retro-button retro-button--ghost"
                      onClick={fetchEligiblePolicies}
                    >
                      RETRY
                    </button>
                  </div>
                </>
              )}

              {eligibleStatus === 'ready' && eligiblePolicies.length === 0 && (
                <p className="dim-line">
                  NOT ONE LAW IS ON THE BOOKS AT ALL. DRAFT ONE FIRST FROM THE
                  DRAFT POLICIES TAB, THEN RETURN HERE TO BIND IT.
                </p>
              )}

              {eligibleStatus === 'ready' && eligiblePolicies.length > 0 && (
                <div className="binding-add-row">
                  <select
                    value={addPolicyKey}
                    onChange={(e) => setAddPolicyKey(e.target.value)}
                    disabled={saveStatus === 'saving' || eligibleOptions.length === 0}
                  >
                    <option value="">
                      {eligibleOptions.length === 0 ? 'ALL LAWS ALREADY BOUND' : 'CHOOSE A LAW\u2026'}
                    </option>
                    {eligibleOptions.map((policy) => {
                      const key = `${policy.owner_username}/${policy.name}`;
                      return (
                        <option key={key} value={key}>
                          {policyOptionLabel(policy)}
                        </option>
                      );
                    })}
                  </select>
                  <button
                    type="button"
                    className="retro-button retro-button--ghost"
                    onClick={handleAddBinding}
                    disabled={!addPolicyKey || saveStatus === 'saving'}
                  >
                    + BIND
                  </button>
                </div>
              )}

              {editFormError && <p className="error-line">! {editFormError}</p>}

              <div className="button-row button-row--split">
                <button
                  type="button"
                  className="retro-button retro-button--ghost"
                  onClick={cancelEditing}
                  disabled={saveStatus === 'saving'}
                >
                  CANCEL
                </button>
                <button type="submit" className="retro-button" disabled={saveStatus === 'saving'}>
                  {saveStatus === 'saving' ? 'REBINDING...' : 'SAVE'}
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
}
